import {
    Slot,
    Toast,
    ToastBody,
    ToastIntent,
    ToastTitle,
    ToastTrigger,
    Link,
    useId,
    useToastController,
    Text,
} from '@fluentui/react-components';
import { ReactNode } from 'react';
import { Case, CaseDocument, CaseEvent, CaseExpense, CaseMilestone, Contact, Invoice, Party, Task, User } from '../../data/types';
import { useUtilities } from '../../utilities/useUtilities';

export const useToasts = (toasterId?: string) => {
    const mainToasterId = useId(undefined, 'main-toast');
    const utils = useUtilities();

    const { dispatchToast } = useToastController(toasterId || mainToasterId);

    //basic functions
    const notify = (intent: ToastIntent, title: string, body: ReactNode, subtitle?: Slot<'div'>) => {
        dispatchToast(
            <Toast>
                <ToastTitle
                    action={
                        <ToastTrigger>
                            <Link>
                                <Text size={200}>Dismiss</Text>
                            </Link>
                        </ToastTrigger>
                    }
                >
                    {title}
                </ToastTitle>
                <ToastBody subtitle={subtitle}>{body}</ToastBody>
            </Toast>,
            { intent },
        );
    };

    const notifySuccess = (title: string, body: ReactNode, subtitle?: Slot<'div'>) =>
        notify('success', title, body, subtitle);
    const notifyError = (title: string, body: ReactNode, subtitle?: Slot<'div'>) => notify('error', title, body, subtitle);
    const notifyInfo = (title: string, body: ReactNode, subtitle?: Slot<'div'>) => notify('info', title, body, subtitle);
    const notifyWarning = (title: string, body: ReactNode, subtitle?: Slot<'div'>) =>
        notify('warning', title, body, subtitle);

    //Account
    const notifyAccountUpdated = () => notifySuccess('Account Updated', `Your account has been updated.`);
    const notifyAccountUpdateError = () =>
        notifyError(
            'Error Updating Account',
            `There was an error updating your account. Please try again.`,
            'If problem persists, contact support.',
        );

    //PW
    const notifyPWresetSuccessful = () =>
        notifySuccess(
            'Password Reset',
            'Your password has been successfully reset. Please login with your new credentials.',
        );
    const notifyPWresetError = () =>
        notifyError(
            'Password Reset Error',
            'There was an error resetting your password. Please try again.',
            'If problem persists, contact support.',
        );

    //Cases
    const notifyCaseCreated = (caseObj: Case) =>
        notifySuccess('Case Created', `Case - ${caseObj.caseName} - has been created.`, `Type: ${caseObj.caseType}`);
    const notifyCaseCreationError = (caseObj: Case) =>
        notifyError(
            'Error Creating Case',
            `There was an error creating Case - ${caseObj.caseName}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseUpdated = (caseObj: Case) =>
        notifySuccess('Case Updated', `Case - ${caseObj.caseName} - has been updated.`, `Type: ${caseObj.caseType}`);
    const notifyCaseUpdateError = (caseObj: Case) =>
        notifyError(
            'Error Updating Case',
            `There was an error updating Case - ${caseObj.caseName}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseDeleted = (caseObj: Case) =>
        notifySuccess('Case Deleted', `Case - ${caseObj.caseName} - has been deleted.`, `Type: ${caseObj.caseType}`);
    const notifyCaseDeleteError = (caseObj: Case) =>
        notifyError(
            'Error Deleting Case',
            `There was an error deleting Case - ${caseObj.caseName}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Engagement
    const notifyCaseEngagementUpdated = (caseObj: Case) =>
        notifySuccess('Case Engagement Updated', `Engagement for Case - ${caseObj.caseName} - has been updated.`);
    const notifyCaseEngagementUpdateError = (caseObj: Case) =>
        notifyError(
            'Error Updating Case Engagement',
            `There was an error updating the Engagement for Case - ${caseObj.caseName}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Case Types
    const notifyCaseTypeCreated = (caseType: string) =>
        notifySuccess('Case Type Added', `Case Type - ${caseType} - has been added to your account.`);
    const notifyCaseTypeCreateError = (caseType: string) =>
        notifyError('Error Adding Case Type', `There was an error adding the Case Type - ${caseType}. Please try again.`);
    const notifyCaseTypeRemoved = (caseType: string) =>
        notifySuccess('Case Type Removed', `Case Type - ${caseType} - has been removed from your account.`);
    const notifyCaseTypeRemovalError = (caseType: string) =>
        notifyError(
            'Error Removing Case Type',
            `There was an error removing the Case Type - ${caseType}. Please try again.`,
        );

    //Case Statuses
    const notifyCaseStatusCreated = (caseStatus: string) =>
        notifySuccess('Case Status Added', `Case Status - ${caseStatus} - has been added to your account.`);
    const notifyCaseStatusCreateError = (caseStatus: string) =>
        notifyError(
            'Error Adding Case Status',
            `There was an error adding the Case Status - ${caseStatus}. Please try again.`,
        );
    const notifyCaseStatusRemoved = (caseStatus: string) =>
        notifySuccess('Case Status Removed', `Case Status - ${caseStatus} - has been removed from your account.`);
    const notifyCaseStatusRemovalError = (caseStatus: string) =>
        notifyError(
            'Error Removing Case Status',
            `There was an error removing the Case Status - ${caseStatus}. Please try again.`,
        );

    //Case Expense Types
    const notifyExpenseTypeCreated = (expenseType: string) =>
        notifySuccess('Expense Type Added', `Expense Type - ${expenseType} - has been added to your account.`);
    const notifyExpenseTypeCreateError = (expenseType: string) =>
        notifyError(
            'Error Adding Expense Type',
            `There was an error adding the Expense Type - ${expenseType}. Please try again.`,
        );
    const notifyExpenseTypeRemoved = (expenseType: string) =>
        notifySuccess('Expense Type Removed', `Expense Type - ${expenseType} - has been removed from your account.`);
    const notifyExpenseTypeRemovalError = (expenseType: string) =>
        notifyError(
            'Error Removing Expense Type',
            `There was an error removing the Expense Type - ${expenseType}. Please try again.`,
        );

    //Parties
    const notifyPartyCreated = (party: Party) =>
        notifySuccess('Party Created', `Party - ${party.name} - has been created for your account.`);
    const notifyPartyCreateError = (party: Party) =>
        notifyError('Error Creating Party', `There was an error creating the Party - ${party.name}. Please try again.`);
    const notifyPartyUpdated = (party: Party) =>
        notifySuccess('Party Updated', `Party - ${party.name} - has been updated in your account.`);
    const notifyPartyUpdateError = (party: Party) =>
        notifyError('Error Updating Party', `There was an error updating the Party - ${party.name}. Please try again.`);
    const notifyPartyDeleted = (party: Party) => notifySuccess('Party Deleted', `Party - ${party.name} - has been deleted.`);
    const notifyPartyDeleteError = (party: Party) =>
        notifyError(
            'Error Deleting Party',
            `There was an error deleting Party - ${party.name}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Contacts
    const notifyContactCreated = (contact: Contact) =>
        notifySuccess('Contact Created', `Contact - ${contact.name} - has been created for your account.`);
    const notifyContactCreateError = (contact: Contact) =>
        notifyError(
            'Error Creating Contact',
            `There was an error creating the Contact - ${contact.name}. Please try again.`,
        );
    const notifyContactUpdated = (contact: Contact) =>
        notifySuccess('Contact Updated', `Contact - ${contact.name} - has been updated in your account.`);
    const notifyContactUpdateError = (contact: Contact) =>
        notifyError(
            'Error Updating Contact',
            `There was an error updating the Contact - ${contact.name}. Please try again.`,
        );
    const notifyContactDeleted = (contact: Contact) =>
        notifySuccess('Contact Deleted', `Contact - ${contact.name} - has been deleted.`);
    const notifyContactDeleteError = (contact: Contact) =>
        notifyError(
            'Error Deleting Contact',
            `There was an error deleting Contact - ${contact.name}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Case Events
    const notifyCaseEventCreated = (caseEvent: CaseEvent) =>
        notifySuccess(
            'Case Event Created',
            `Case Event - ${caseEvent.event} - has been created.`,
            `Date: ${caseEvent.date}`,
        );
    const notifyCaseEventCreateError = (caseEvent: CaseEvent) =>
        notifyError(
            'Error Creating Case Event',
            `There was an error creating the Case Event - ${caseEvent.event}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseEventUpdated = (caseEvent: CaseEvent) =>
        notifySuccess(
            'Case Event Updated',
            `Case Event - ${caseEvent.event} - has been updated.`,
            `Date: ${caseEvent.date}`,
        );
    const notifyCaseEventUpdateError = (caseEvent: CaseEvent) =>
        notifyError(
            'Error Updating Case Event',
            `There was an error updating the Case Event - ${caseEvent.event}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseEventDeleted = (caseEvent: CaseEvent) =>
        notifySuccess(
            'Case Event Deleted',
            `Case Event - ${caseEvent.event} - has been deleted.`,
            `Date: ${caseEvent.date}`,
        );
    const notifyCaseEventDeleteError = (caseEvent: CaseEvent) =>
        notifyError(
            'Error Deleting Case Event',
            `There was an error deleting the Case Event - ${caseEvent.event}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Tasks
    const notifyTaskCreated = (task: Task) =>
        notifySuccess(
            'Task Created',
            `Task - ${task.title?.substring(0,30)}... - has been created.`,
        );
    const notifyTaskCreateError = (task: Task) =>
        notifyError(
            'Error Creating Task',
            `There was an error creating the Task - ${task.title?.substring(0,30)}.... Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyTaskUpdated = (task: Task) =>
        notifySuccess(
            'Task Updated',
            `Task - ${task.title?.substring(0,30)}... - has been updated.`,
        );
    const notifyTaskUpdateError = (task: Task) =>
        notifyError(
            'Error Updating Task',
            `There was an error updating the Task - ${task.title?.substring(0,30)}.... Please try again.`,
            'If problem persists, contact support.',
        );

    //Case Notes
    const notifyCaseNoteCreated = () => notifySuccess('Case Note Created', `Case Note has been created.`);
    const notifyCaseNoteCreateError = () =>
        notifyError(
            'Error Creating Case Note',
            `There was an error creating the Case Note. Please try again.`,
            'If problem persists, contact support.',
        );

    //Case Documents
    const notifyCaseDocUpdated = (caseDoc: CaseDocument) =>
        notifySuccess('Case Document Updated', `Case Document - ${caseDoc.name} - has been updated.`);
    const notifyCaseDocUpdatedError = (caseDoc: CaseDocument) =>
        notifyError(
            'Error Updating Case Document',
            `There was an error updating the Case Document - ${caseDoc.name}. Please try again.`,
            'If problem persists, contact support.',
        );

    const notifyCaseDocDeleted = (caseDoc: CaseDocument) =>
        notifySuccess('Case Document Deleted', `Case Document - ${caseDoc.name} - has been deleted.`);
    const notifyCaseDocsDeleted = (caseDocs: CaseDocument[]) =>
        notifySuccess('Case Documents Deleted', `${caseDocs.length} Case Documents have been deleted.`);
    const notifyCaseDocDeleteError = () =>
        notifyError(
            'Error Deleting Case Documents',
            `There was an error deleting the Case Documents. Please try again.`,
            'If problem persists, contact support.',
        );

    //Invoices
    const notifyInvoiceCreated = (invoice: Invoice) =>
        notifySuccess(
            'Invoice Created',
            `Invoice - ${invoice.invoiceNumber} - has been created.`,
            `Date: ${invoice.invoiceDate}`,
        );
    const notifyInvoiceUpdated = (invoice: Invoice) =>
        notifySuccess(
            'Invoice Updated',
            'Invoice was updated.',
            `${invoice.invoiceNumber} - ${utils.date.formatDate(invoice.invoiceDate, undefined, true)}`,
        );
    const notifyInvoiceCreateError = (invoice: Invoice) =>
        notifyError(
            'Error Creating Invoice',
            `There was an error creating the Invoice - ${invoice.invoiceNumber}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyInvoiceDeleted = (invoice: Invoice) =>
        notifySuccess(
            'Invoice Deleted',
            `Invoice - ${invoice.invoiceNumber} - has been deleted.`,
            `Date: ${invoice.invoiceDate}`,
        );
    const notifyInvoicesDeleted = (invoiceCount: number) =>
        notifySuccess('Invoices Deleted', `${invoiceCount} Invoices have been deleted.`);
    const notifyInvoiceDeleteError = () =>
        notifyError(
            'Error Deleting Invoice',
            `There was an error deleting the Invoice. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyInvoiceEmailed = (invoice: Invoice, contact: Contact) =>
        notifySuccess(
            'Invoice Emailed',
            `Invoice - ${invoice.invoiceNumber} - was emailed to ${contact.name || contact.email}.`,
            contact.email,
        );
    const notifyInvoiceEmailError = (invoice: Invoice) =>
        notifyError(
            'Error Emailing Invoice',
            `There was an error emailing Invoice - ${invoice.invoiceNumber}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Case Expenses
    const notifyCaseExpenseCreated = (caseExp: CaseExpense) =>
        notifySuccess('Case Expense Created', `Case Expense - ${caseExp.type} - has been created.`, `Date: ${caseExp.date}`);
    const notifyCaseExpenseCreateError = (caseExp: CaseExpense) =>
        notifyError(
            'Error Creating Case Expense',
            `There was an error creating the Case Expense - ${caseExp.type}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseExpenseDeleted = (caseExp: CaseExpense) =>
        notifySuccess('Case Expense Deleted', `Case Expense - ${caseExp.type} - has been deleted.`, `Date: ${caseExp.date}`);
    const notifyCaseExpenseDeleteError = (caseExp: CaseExpense) =>
        notifyError(
            'Error Deleting Case Expense',
            `There was an error deleting the Case Expense - ${caseExp.type}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseExpensesDeleted = (caseExps: CaseExpense[]) =>
        notifySuccess('Case Expenses Deleted', `${caseExps.length} Case Expenses have been deleted.`);
    const notifyCaseExpensesDeleteError = () =>
        notifyError(
            'Error Deleting Case Expenses',
            `There was an error deleting the Case Expenses. Please try again.`,
            'If problem persists, contact support.',
        );

    //Users
    const notifyUserAdded = (user: User) =>
        notifySuccess('User Added', `${user.name} has been added to your account.`, user.username);
    const notifyUserAddError = (user: User) =>
        notifyError(
            'Error Adding User',
            `There was an error adding ${user.name}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyUserUpdated = (user: User) => notifySuccess('User Updated', `${user.name} has been updated.`, user.username);
    const notifyUserUpdatedError = (user: User) =>
        notifyError(
            'Error Updating User',
            `There was an error updating ${user.name}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyUserDeleted = (user: User) => notifySuccess('User Deleted', `${user.name} has been deleted.`, user.username);
    const notifyUserDeletedError = (user: User) =>
        notifyError(
            'Error Deleting User',
            `There was an error deleting ${user.name}. Please try again.`,
            'If problem persists, contact support.',
        );

    //Case Milestones
    const notifyCaseMilestoneCreated = (caseMilestone: CaseMilestone) =>
        notifySuccess('Case Milestone Created', `Case Milestone - ${caseMilestone.name} - has been created.`);
    const notifyCaseMilestoneCreateError = (caseMilestone: CaseMilestone) =>
        notifyError(
            'Error Creating Case Milestone',
            `There was an error creating the Case Milestone - ${caseMilestone.name}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseMilestoneUpdated = (caseMilestone: CaseMilestone) =>
        notifySuccess('Case Milestone Updated', `Case Milestone - ${caseMilestone.name} - has been updated.`);
    const notifyCaseMilestoneUpdateError = (caseMilestone: CaseMilestone) =>
        notifyError(
            'Error Updating Case Milestone',
            `There was an error updating the Case Milestone - ${caseMilestone.name}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseMilestoneDeleted = (caseMilestone: CaseMilestone) =>
        notifySuccess('Case Milestone Deleted', `Case Milestone - ${caseMilestone.name} - has been deleted.`);
    const notifyCaseMilestoneDeleteError = (caseMilestone: CaseMilestone) =>
        notifyError(
            'Error Deleting Case Milestone',
            `There was an error deleting the Case Milestone - ${caseMilestone.name}. Please try again.`,
            'If problem persists, contact support.',
        );
    const notifyCaseMilestonesDeleted = (milestoneCount: number) =>
        notifySuccess('Case Milestones Deleted', `${milestoneCount} Case Milestones have been deleted.`);
    const notifyCaseMilestonesDeleteError = () =>
        notifyError(
            'Error Deleting Case Milestones',
            `There was an error deleting the Case Milestones. Please try again.`,
            'If problem persists, contact support.',
        );

    return {
        notify,
        notifySuccess,
        notifyWarning,
        notifyInfo,
        notifyError,
        account: {
            notifyAccountUpdated,
            notifyAccountUpdateError,
        },
        pw: {
            notifyPWresetSuccessful,
            notifyPWresetError,
        },
        cases: {
            notifyCaseCreated,
            notifyCaseCreationError,
            notifyCaseUpdated,
            notifyCaseUpdateError,
            notifyCaseDeleted,
            notifyCaseDeleteError,
        },
        engagement: {
            notifyCaseEngagementUpdated,
            notifyCaseEngagementUpdateError,
        },
        caseTypes: {
            notifyCaseTypeCreated,
            notifyCaseTypeCreateError,
            notifyCaseTypeRemoved,
            notifyCaseTypeRemovalError,
        },
        caseStatus: {
            notifyCaseStatusCreated,
            notifyCaseStatusCreateError,
            notifyCaseStatusRemoved,
            notifyCaseStatusRemovalError,
        },
        expenseTypes: {
            notifyExpenseTypeCreated,
            notifyExpenseTypeCreateError,
            notifyExpenseTypeRemoved,
            notifyExpenseTypeRemovalError,
        },
        parties: {
            notifyPartyCreated,
            notifyPartyCreateError,
            notifyPartyUpdated,
            notifyPartyUpdateError,
            notifyPartyDeleted,
            notifyPartyDeleteError,
        },
        contacts: {
            notifyContactCreated,
            notifyContactCreateError,
            notifyContactUpdated,
            notifyContactUpdateError,
            notifyContactDeleted,
            notifyContactDeleteError,
        },
        caseEvents: {
            notifyCaseEventCreated,
            notifyCaseEventCreateError,
            notifyCaseEventUpdated,
            notifyCaseEventUpdateError,
            notifyCaseEventDeleted,
            notifyCaseEventDeleteError,
        },
        caseNotes: {
            notifyCaseNoteCreated,
            notifyCaseNoteCreateError,
        },
        caseDocuments: {
            notifyCaseDocUpdated,
            notifyCaseDocUpdatedError,
            notifyCaseDocDeleted,
            notifyCaseDocsDeleted,
            notifyCaseDocDeleteError,
        },
        caseExpenses: {
            notifyCaseExpenseCreated,
            notifyCaseExpenseCreateError,
            notifyCaseExpenseDeleted,
            notifyCaseExpenseDeleteError,
            notifyCaseExpensesDeleted,
            notifyCaseExpensesDeleteError,
        },
        users: {
            notifyUserAdded,
            notifyUserAddError,
            notifyUserDeleted,
            notifyUserDeletedError,
            notifyUserUpdated,
            notifyUserUpdatedError,
        },
        invoices: {
            notifyInvoiceCreated,
            notifyInvoiceCreateError,
            notifyInvoiceUpdated,
            notifyInvoiceDeleted,
            notifyInvoicesDeleted,
            notifyInvoiceDeleteError,
            notifyInvoiceEmailed,
            notifyInvoiceEmailError,
        },
        caseMilestones: {
            notifyCaseMilestoneCreated,
            notifyCaseMilestoneCreateError,
            notifyCaseMilestoneUpdated,
            notifyCaseMilestoneUpdateError,
            notifyCaseMilestoneDeleted,
            notifyCaseMilestoneDeleteError,
            notifyCaseMilestonesDeleted,
            notifyCaseMilestonesDeleteError,
        },
        tasks: {
            notifyTaskCreated,
            notifyTaskCreateError,
            notifyTaskUpdated,
            notifyTaskUpdateError,
        }
    };
};
